import { FormikProvider, useField, useFormik } from 'formik';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex } from 'reflexbox';

import { useFeature, useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { FeatureName } from '@treams/treams-network';
import { Checkbox, Select } from '@treams/treams-ui';

import FilledInBy from './common/FilledInBy';
import Property from './common/Property';
import Title from './common/Title';

function CompetencyLevelSet() {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const [field, , { setValue }] = useField<graphql.CompetencyLevelSet | undefined>(
		'competencyLevelSet',
	);

	const { data } = graphql.useCompetencyLevelSetsQuery({
		variables: {
			isArchived: false,
			first: 1000,
		},
	});

	const options =
		data?.competencyLevelSets.edges.map((edge) => ({
			value: edge.node.id,
			label: localize(edge.node.title).text,
		})) ?? [];

	return (
		<Property
			title={t('common.settings.evaluationForms.properties.finalScore.competencyLevelSet.label')}
		>
			<Select
				options={options.map(({ value, label }) => ({
					title: label,
					value,
				}))}
				selectedValue={field.value?.id}
				onChange={(value) => {
					const set = data?.competencyLevelSets.edges.find((edge) => edge.node.id === value);
					setValue(set?.node as graphql.CompetencyLevelSet);
				}}
			/>
		</Property>
	);
}

function DisplayLevelSet() {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const [field, , { setValue }] = useField<graphql.CompetencyLevelSet | undefined>(
		'displayLevelSet',
	);

	const { data } = graphql.useCompetencyLevelSetsQuery({
		variables: {
			isArchived: false,
			first: 1000,
		},
	});

	const options =
		data?.competencyLevelSets.edges.map((edge) => ({
			value: edge.node.id,
			label: localize(edge.node.title).text,
		})) ?? [];

	return (
		<Property
			title={t('common.settings.evaluationForms.properties.finalScore.displayLevelSet.label')}
		>
			<Select
				options={options.map(({ value, label }) => ({
					title: label,
					value,
				}))}
				selectedValue={field.value?.id}
				onChange={(value) => {
					const set = data?.competencyLevelSets.edges.find((edge) => edge.node.id === value);
					setValue(set?.node as graphql.CompetencyLevelSet);
				}}
				clearable
			/>
		</Property>
	);
}

function IncludeReviewGoals() {
	const { t } = useTranslation();
	const [field, , { setValue }] = useField<boolean>('includeReviewGoals');

	return (
		<Property
			title={t('common.settings.evaluationForms.properties.finalScore.includeReviewGoals.label')}
		>
			<Checkbox checked={field.value} onClick={() => setValue(!field.value)} />
		</Property>
	);
}

function CalculationMethod() {
	const { t } = useTranslation();
	const [field, , { setValue }] =
		useField<graphql.EvaluationFinalScoreCalculationMethod>('calculationMethod');

	const options = Object.values(graphql.EvaluationFinalScoreCalculationMethod).map((method) => ({
		title: t(
			`common.settings.evaluationForms.properties.finalScore.calculationMethod.${method.toLowerCase()}`,
		),
		value: method,
	}));

	return (
		<Property
			title={t('common.settings.evaluationForms.properties.finalScore.calculationMethod.label')}
		>
			<Select
				options={options.map(({ value, title }) => ({
					title,
					value,
				}))}
				selectedValue={field.value}
				onChange={(value) => setValue(value as graphql.EvaluationFinalScoreCalculationMethod)}
			/>
		</Property>
	);
}

function ScoreFormat() {
	const { t } = useTranslation();
	const [field, , { setValue }] = useField<graphql.EvaluationFinalScoreFormat>('scoreFormat');

	return (
		<Property title={t('common.settings.evaluationForms.properties.finalScore.scoreFormat.label')}>
			<Select
				options={[
					{
						title: t('common.settings.evaluationForms.properties.finalScore.scoreFormat.integer'),
						value: graphql.EvaluationFinalScoreFormat.Integer,
					},
					{
						title: t('common.settings.evaluationForms.properties.finalScore.scoreFormat.decimal'),
						value: graphql.EvaluationFinalScoreFormat.Decimal,
					},
				]}
				selectedValue={field.value}
				onChange={(value) => setValue(value as graphql.EvaluationFinalScoreFormat)}
			/>
		</Property>
	);
}

export default function FinalScoreModal(props: {
	fillInBy: graphql.EvaluationFillInBy;
	item: graphql.EvaluationFinalScoreItem;
	onChange: (item: graphql.EvaluationFinalScoreItem, fillInBy: graphql.EvaluationFillInBy) => void;
}) {
	const hasFinalScoreSumCalculation = useFeature(FeatureName.FinalScoreSumCalculation);
	const formik = useFormik({
		initialValues: {
			title: props.item.title,
			competencyLevelSet: props.item.competencyLevelSet,
			displayLevelSet: props.item.displayLevelSet,
			fillInBy: props.fillInBy,
			includeReviewGoals: props.item.includeReviewGoals ?? false,
			calculationMethod: props.item.calculationMethod,
			scoreFormat: props.item.scoreFormat ?? graphql.EvaluationFinalScoreFormat.Integer,
		},
		onSubmit: () => {},
	});

	useEffect(() => {
		if (!formik.values.competencyLevelSet) {
			return;
		}

		props.onChange(
			{
				...props.item,
				title: formik.values.title,
				competencyLevelSet: formik.values.competencyLevelSet,
				displayLevelSet: formik.values.displayLevelSet,
				includeReviewGoals: formik.values.includeReviewGoals,
				calculationMethod: formik.values.calculationMethod,
				scoreFormat: formik.values.scoreFormat,
			},
			formik.values.fillInBy,
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		formik.values.title,
		formik.values.fillInBy,
		formik.values.competencyLevelSet?.id,
		formik.values.displayLevelSet?.id,
		formik.values.includeReviewGoals,
		formik.values.calculationMethod,
		formik.values.scoreFormat,
	]);

	return (
		<FormikProvider value={formik}>
			<Flex p={3} flexDirection="column">
				<Title />
				<FilledInBy />
				<CompetencyLevelSet />
				<DisplayLevelSet />
				{hasFinalScoreSumCalculation && <CalculationMethod />}
				<ScoreFormat />
				<IncludeReviewGoals />
			</Flex>
		</FormikProvider>
	);
}
